import { clearPin } from '../lib/security/app-lock';
import { clearServerContact } from '../lib/session';
import { clearCachedMe } from './me';
import { markSignedOut } from '../lib/auth/iap';
import { clearDeviceOwner } from '../lib/device-owner';
import type { Services } from './services';

/** How long a wipe waits for the outbox to empty (ADR-0021). */
export const WIPE_DRAIN_TIMEOUT_MS = 60_000;

/**
 * Account disabled or wipe requested (ADR-0021): upload whatever is still
 * queued — backed-off and once-refused items included, for up to a minute
 * (the API accepts uploads for a grace period) — then remove all local data
 * and sign out. If anything could not be sent, nothing is deleted: the data
 * stays on the device, locked behind sign-in, and the caller tells the user
 * (`keptPending` > 0). Losing captured work silently is never acceptable.
 */
export async function wipeDevice(s: Services, timeoutMs = WIPE_DRAIN_TIMEOUT_MS): Promise<{ keptPending: number }> {
  let remaining: number;
  try {
    remaining = (await s.sync.drain(timeoutMs)).remaining;
  } catch {
    remaining = (await s.storage.all().catch(() => [])).length;
  }
  s.sync.stop();
  if (remaining === 0) {
    await s.storage.wipe();
    clearPin();
    clearDeviceOwner();
    clearServerContact();
  } else {
    await clearCachedMe(s.storage);
  }
  s.queryClient.clear();
  await s.auth.signOut();
  return { keptPending: remaining };
}

/**
 * Voluntary sign-out. With nothing waiting to sync the device is cleaned
 * completely; otherwise local work is kept (locked behind sign-in) so it is
 * not lost.
 */
export async function signOut(s: Services): Promise<{ keptPending: number }> {
  // Deliberate sign-out: don't auto sign in via IAP again, so another account can be chosen.
  markSignedOut();
  const status = await s.sync.refresh();
  const keptPending = status.pending + status.rejected;
  s.sync.stop();
  if (keptPending === 0) {
    await s.storage.wipe();
    clearPin();
    clearDeviceOwner();
  }
  await clearCachedMe(s.storage);
  s.queryClient.clear();
  await s.auth.signOut();
  return { keptPending };
}
